import { useTranslation } from 'react-i18next';
import { StyleProp, TextStyle, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';
import { tokens } from '../../../crop-circle/tokens';
import { themedStyleSheet } from '../../../../themes/themedStyleSheet';

/**
 * The IDP chrome the Activity message renders — plan 0027, Phase 3
 * "Lock-until-terminal".
 *
 * Kept together (and apart from `workflowMessageQuestion`) because they are
 * the visual contract of the feature's reviewer-facing payoff: what an
 * IDP-managed field looks like while extraction runs, what a slow job says,
 * and what a locked edit explains. Changing any of them is a design decision,
 * not a rendering detail. (An "AI-assisted" badge on terminal values shipped
 * here originally and was removed on Peter's call, 2026-07-28 — terminal
 * managed fields render with no notation.)
 */

/**
 * In place of a managed field's value while `idp.status` is non-terminal. The
 * value is deliberately absent rather than blank: a reviewer must never see a
 * half-baked or empty-looking answer that is actually still being extracted.
 */
export const IdpExtractingValue = ({ testID }: { testID?: string }) => {
  const { t } = useTranslation();
  const label = t('routines.message.idp.extracting');

  return (
    <View
      style={styles.extractingRow}
      testID={testID}
      accessibilityRole="progressbar"
      accessibilityLabel={label}
    >
      <ActivityIndicator
        animating={true}
        size={16}
        color={tokens.colors.brand.core}
      />
      <Text style={styles.extractingText}>{label}</Text>
    </View>
  );
};

/**
 * Reassurance for a job that has outlived the grace period. It is a hint, not
 * an error: the worker terminalizes on its final Service Bus delivery attempt,
 * so the fields resolve themselves without the reviewer doing anything.
 */
export const IdpTakingLongerHint = ({ testID }: { testID?: string }) => {
  const { t } = useTranslation();

  return (
    <Text style={styles.takingLongerText} testID={testID}>
      {t('routines.message.idp.takingLonger')}
    </Text>
  );
};

/**
 * Explains a disabled revise/edit affordance while extraction is in flight —
 * the UI face of the server's ROUTINE_IDP_IN_PROGRESS lock. One definition so
 * the surfaces that show it (web revise card, web revise route, mobile edit
 * tabs) cannot drift apart; spacing is the caller's, via `style`.
 */
export const IdpEditLockedHint = ({
  testID,
  style,
}: {
  testID?: string;
  style?: StyleProp<TextStyle>;
}) => {
  const { t } = useTranslation();

  return (
    <Text style={[styles.editLockedText, style]} testID={testID}>
      {t('routines.message.idp.editLocked')}
    </Text>
  );
};

const styles = themedStyleSheet((role) => ({
  extractingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    marginTop: tokens.spacing.xs,
  },
  extractingText: {
    ...tokens.typography.label.medium,
    color: role.textSecondary,
  },
  takingLongerText: {
    ...tokens.typography.label.medium,
    color: role.textSecondary,
    marginTop: tokens.spacing.sm,
  },
  editLockedText: {
    ...tokens.typography.label.medium,
    color: role.textSecondary,
  },
}));
